import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { HiArrowRight } from 'react-icons/hi';
import { scrollToTop } from '../../utils/scrollUtils';
import { setClickSource } from '../../services/analytics/analyticsService';
import { ShowcaseCard, SkeletonCard } from './HeroShowcase';
import {
  GUTTER, PANEL_GAP, SURFACE, RED_SHADOW, DotGrid, RedSurfaceDecor, Eyebrow, SectionTitle, PillButton,
} from './landingUi';

// Horizontal padding of the panel. The rail reuses it as its own padding (and
// scroll-padding) so the first card lines up with the heading while the rail
// itself still runs to the panel's edges.
const PAD_X = 'px-[clamp(1.25rem,5vw,3rem)]';
const SCROLL_PAD = 'scroll-px-[clamp(1.25rem,5vw,3rem)]';

/**
 * A titled panel with a swipeable row of product cards — the mobile form of
 * the hero's showcase lanes, used for the bestseller and offers sections.
 *
 *   tone="surface"  off-white with a dot grid (bestsellers, with ranks)
 *   tone="red"      brand red with white type (offers, with % stickers)
 *
 * Cards are the hero's ShowcaseCard at a fixed height, so a product looks the
 * same in the hero, in a campaign and here.
 */
const ProductRailPanel = ({
  tone = 'surface',
  eyebrow,
  lead,
  highlight,
  subtitle,
  items,
  loading,
  variant = 'bestseller',
  showRank = false,
  viewAllLabel,
  viewAllPath,
  ariaLabel,
}) => {
  const navigate = useNavigate();
  const onRed = tone === 'red';

  if (!loading && items.length === 0) return null;

  const go = (path) => { navigate(path); setTimeout(() => scrollToTop(), 100); };

  return (
    <section className={`${GUTTER} ${PANEL_GAP}`} aria-label={ariaLabel || `${lead || ''} ${highlight || ''}`.trim()}>
      <motion.div
        className={`relative overflow-hidden rounded-[2rem] py-[clamp(1.5rem,5vw,3rem)] lg:rounded-[2.5rem] ${
          onRed ? `bg-brand text-white ${RED_SHADOW}` : 'ring-1 ring-black/[0.04]'
        }`}
        style={onRed ? undefined : { backgroundColor: SURFACE }}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        {onRed ? <RedSurfaceDecor radius="rounded-[2rem] lg:rounded-[2.5rem]" /> : <DotGrid radius="rounded-[2rem] lg:rounded-[2.5rem]" />}

        {/* Heading */}
        <header className={`relative flex items-end justify-between gap-4 ${PAD_X}`}>
          <div className="min-w-0">
            {eyebrow && <Eyebrow tone={onRed ? 'onRed' : 'light'} pulse={onRed}>{eyebrow}</Eyebrow>}
            <SectionTitle className="mt-3" tone={onRed ? 'onRed' : 'light'} lead={lead} highlight={highlight} />
            {subtitle && (
              <p className={`mt-2 max-w-md text-[14px] leading-relaxed ${onRed ? 'text-white/80' : 'text-gray-500'}`}>
                {subtitle}
              </p>
            )}
          </div>
          {viewAllPath && (
            <button
              type="button"
              onClick={() => go(viewAllPath)}
              aria-label={viewAllLabel}
              className={`inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full transition-all duration-200 active:scale-95 focus:outline-none focus-visible:ring-2 ${
                onRed
                  ? 'bg-white text-brand shadow-[0_10px_22px_-12px_rgb(var(--brand-shadow-deep)/0.8)] focus-visible:ring-white/70'
                  : 'bg-brand text-white shadow-[0_10px_22px_-12px_rgb(var(--brand)/0.8)] focus-visible:ring-brand/40'
              }`}
            >
              <HiArrowRight className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
        </header>

        {/* Rail */}
        <div
          className={`product-rail relative mt-5 flex snap-x snap-mandatory gap-2.5 overflow-x-auto pb-3 pt-1 sm:gap-3 ${PAD_X} ${SCROLL_PAD}`}
        >
          <style>{`.product-rail { scrollbar-width: none; -webkit-overflow-scrolling: touch; } .product-rail::-webkit-scrollbar { display: none; }`}</style>
          {loading
            ? [0, 1, 2].map((i) => (
                <div key={i} className="h-[clamp(13.5rem,56vw,18rem)] flex-shrink-0">
                  <SkeletonCard />
                </div>
              ))
            : items.map((item, index) => (
                <div key={item.id || index} className="h-[clamp(13.5rem,56vw,18rem)] flex-shrink-0 snap-start">
                  <ShowcaseCard
                    item={item}
                    rank={showRank ? index + 1 : undefined}
                    variant={variant}
                    onSelect={(code) => {
                      setClickSource(variant === 'deal' ? 'home_discounts' : 'home_bestsellers');
                      navigate(`/product/${code}`);
                    }}
                  />
                </div>
              ))}
          {/* Trailing spacer: padding-right on a scroll container is ignored
              by some mobile browsers, so the last card would sit flush. */}
          <div className="w-px flex-shrink-0" aria-hidden="true" />
        </div>

        {viewAllPath && viewAllLabel && (
          <div className={`relative mt-4 ${PAD_X}`}>
            <PillButton
              variant={onRed ? 'white' : 'solid'}
              onClick={() => go(viewAllPath)}
              className="w-full justify-center sm:w-auto"
            >
              {viewAllLabel}
            </PillButton>
          </div>
        )}
      </motion.div>
    </section>
  );
};

export default ProductRailPanel;
